import DragIndicatorIcon from '@mui/icons-material/DragIndicator'
import React from 'react'
import styled from 'styled-components'
import { DragDropProvider, DragEndEvent, PointerSensor } from '@dnd-kit/react'
import { useSortable } from '@dnd-kit/react/sortable'

import { Button } from '@nexiahome/ui-common/components/primitives/buttons'

import { color } from 'styles/colors'
import { Circle } from 'styles/shared'
import type { List } from 'components/ListSelectAndSort'

import Checkbox from 'components/Checkbox'

type ItemProps = {
  value: List
  itemIndex: number
  showTooltipCheckbox: boolean
  moveItemToAvailable: (itemIndex: number) => void
  onChangeCallback: (title: string) => void
}

type ListProps = {
  items: List[]
  showTooltipCheckbox: boolean
  moveItemToAvailable: (itemIndex: number) => void
  onChangeCallback: (title: string) => void
}

type SortableListProps = ListProps & {
  onSortEnd: ({ oldIndex, newIndex }: { oldIndex: number, newIndex: number }) => void
  useDragHandle?: boolean
}

const Item = ({ value, itemIndex, moveItemToAvailable, onChangeCallback, showTooltipCheckbox = false }: ItemProps) => {
  const sortable = useSortable({
    id: value.id,
    index: itemIndex
  })

  return (
    <S.Item ref={sortable.ref}>
      <S.ItemDragAndName>
        <S.DragHandleButton type="button" ref={sortable.handleRef} aria-label={`Reorder ${value.title}`}>
          <S.DragHandle />
        </S.DragHandleButton>
        <div>
          <S.ItemName>
            {value?.color && <Circle color={value.color} />}
            <span>{value.title}</span>
            {showTooltipCheckbox && value?.color &&
              <S.Checkbox
                label="Show in Tooltip Alone"
                checked={value?.showInTooltip || false}
                listId={`checkbox-${value.title}`}
                onChange={() => { onChangeCallback(value.title) }}
              />
            }
          </S.ItemName>
          <S.TagContainer>
            {value?.tiers && value.tiers.map(tier => <S.Tag key={tier}>{tier}</S.Tag>)}
            {value?.tag && <S.Tag>{value.tag}</S.Tag>}
          </S.TagContainer>
        </div>
      </S.ItemDragAndName>
      <Button
        decoration="danger-secondary"
        key={value.id}
        text="Remove"
        onPress={(e) => {
          moveItemToAvailable(itemIndex)
          e.preventDefault()
          e.stopPropagation()
        }}
      />
    </S.Item>
  )
}

const List = ({ items, moveItemToAvailable, onChangeCallback, showTooltipCheckbox }: ListProps) => {
  return (
    <S.Items>
      {items.map((value, index) => (
        <Item
          key={`item-${value.id}`}
          value={value}
          itemIndex={index}
          showTooltipCheckbox={showTooltipCheckbox}
          moveItemToAvailable={moveItemToAvailable}
          onChangeCallback={onChangeCallback}
        />
      ))}
    </S.Items>
  )
}

const SortableList = ({ items, moveItemToAvailable, onChangeCallback, showTooltipCheckbox, onSortEnd }: SortableListProps) => {
  const handleDragEnd = (event: DragEndEvent) => {
    if (event.canceled) return

    const { source, target } = event.operation
    if (!source || !target || source.id === target.id) return

    const oldIndex = items.findIndex((item) => item.id === source.id)
    const newIndex = items.findIndex((item) => item.id === target.id)

    if (oldIndex !== -1 && newIndex !== -1) {
      onSortEnd({ oldIndex, newIndex })
    }
  }

  return (
    <DragDropProvider sensors={[PointerSensor]} onDragEnd={handleDragEnd}>
      <List
        items={items}
        moveItemToAvailable={moveItemToAvailable}
        onChangeCallback={onChangeCallback}
        showTooltipCheckbox={showTooltipCheckbox}
      />
    </DragDropProvider>
  )
}

export default SortableList

const S = {
  Items: styled.div`
    display: flex;
    flex-direction: column;
    border-radius: 4px;
    overflow: auto;
    height: 100%;
    min-width: 250px;
    padding: 25px;
  `,
  Item: styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    user-select: none;
    border: 1px solid ${color('border')};
    padding: 10px 15px;
    background-color: ${color('paneBackground')};
    text-wrap: wrap;
    justify-content: space-between;

    > button {
      margin-left: 20px;
    }
  `,
  ItemName: styled.div`
    display: flex;
    align-items: center;
  `,
  TagContainer: styled.div`
    margin-top: 5px;
    display: flex;
    align-items: center;
    gap: 5px;
  `,
  Tag: styled.span`
    background-color: ${color('tagBackground')};
    color: ${color('tagText')};
    border-radius: 5px;
    padding: 0 5px;
    font-weight: bold;
    cursor: default;
    border: 1px solid ${color('tagBorder')};
  `,
  ItemDragAndName: styled.div`
    display: flex;
    align-items: center;
  `,
  Checkbox: styled(Checkbox)`
    border-left: 1px solid ${color('border')};
    padding: 10px;
    margin: 0px;
    margin-left: 10px;
  `,
  DragHandle: styled(DragIndicatorIcon)`
    cursor: grab;
    margin-right: 10px;
  `,
  DragHandleButton: styled.button`
    border: 0;
    background: transparent;
    padding: 0;
    display: inline-flex;
    align-items: center;
    cursor: grab;
  `
}
